import axios from "axios";
import {
  AlertCircle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  Package,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { confirmAction, showError, showSuccess } from "../../../utils/toastUtils";

const ITEMS_PER_PAGE = 10;

// EXPORT TO EXCEL
// EXPORT TO EXCEL
const exportToExcel = async (requests) => {
  try {
    showSuccess("Preparing Excel export...");
    const XLSX = await import("xlsx");

    const exportData = [];
    let slNo = 1;

    requests.forEach((request) => {
      request.products.forEach((item) => {
        const isUseAndThrow = item.product?.productUsageType === "use_and_throw";

        exportData.push({
          "Sl No": slNo++,
          "Request ID": `#${request._id.slice(-8).toUpperCase()}`,
          Branch: request.branch?.branchName || "-",
          Staff: request.staff?.name || "-",
          "Request Date": new Date(request.createdAt).toLocaleDateString("en-IN"),
          "Request Status": request.status,
          "Product Name": item.product?.productName || "-",
          Brand: item.product?.brand || "-",
          "Requested Quantity": item.requestedQuantity,
          "Assigned Quantity": item.assignedQuantity || 0,
          "Returned Quantity": isUseAndThrow ? "Not Required" : (item.returnedQuantity || 0),
          "Damaged Quantity": isUseAndThrow ? "Not Required" : (item.damagedQuantity || 0),
          "Usage Type": isUseAndThrow ? "Use & Throw" : "Reusable",
          "Product Status": item.status,
          "Return Status": isUseAndThrow
            ? "Not Required"
            : item.returnStatus === "partially_returned"
              ? "Partially Returned"
              : item.returnStatus
                ? item.returnStatus.charAt(0).toUpperCase() + item.returnStatus.slice(1)
                : "Pending",
          "Return Remark": isUseAndThrow ? "-" : (item.returnRemark || "-"),
        });
      });
    });

    // Create main worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Improved column widths (adjusted for new columns)
    const columnWidths = [
      { wch: 8 },    // Sl No
      { wch: 15 },   // Request ID
      { wch: 20 },   // Branch
      { wch: 18 },   // Staff
      { wch: 14 },   // Request Date
      { wch: 15 },   // Request Status
      { wch: 28 },   // Product Name
      { wch: 15 },   // Brand
      { wch: 16 },   // Requested Quantity
      { wch: 16 },   // Assigned Quantity          
      { wch: 16 },   // Returned Quantity           
      { wch: 16 },   // Damaged Quantity            
      { wch: 15 },   // Usage Type
      { wch: 15 },   // Product Status
      { wch: 18 },   // Return Status
    ];

    worksheet["!cols"] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Product Requests");

    // Summary sheet (enhanced with return stats)
    const totalProducts = requests.reduce((sum, r) => sum + r.products.length, 0);
    const pendingRequests = requests.filter(r => r.status === "pending").length;
    const verifiedRequests = requests.filter(r => r.status === "verified").length;
    const approvedRequests = requests.filter(r => r.status === "approved").length;
    const rejectedRequests = requests.filter(r => r.status === "rejected").length;

    // New summary metrics for returns
    let totalReturned = 0;
    let totalDamaged = 0;
    let totalPartiallyReturned = 0;

    requests.forEach(request => {
      request.products.forEach(item => {
        if (item.returnedQuantity > 0) totalReturned += item.returnedQuantity;
        if (item.damagedQuantity > 0) totalDamaged += item.damagedQuantity;
        if (item.returnStatus === "partially_returned") totalPartiallyReturned++;
      });
    });

    const summaryData = [
      { Metric: "Total Requests", Value: requests.length },
      { Metric: "Total Products", Value: totalProducts },
      { Metric: "Pending Requests", Value: pendingRequests },
      { Metric: "Verified Requests", Value: verifiedRequests },
      { Metric: "Approved Requests", Value: approvedRequests },
      { Metric: "Rejected Requests", Value: rejectedRequests },
      { Metric: "Total Returned Quantity", Value: totalReturned },
      { Metric: "Total Damaged Quantity", Value: totalDamaged },
      { Metric: "Partially Returned Items", Value: totalPartiallyReturned },
      { Metric: "Exported On", Value: new Date().toLocaleString("en-IN") },
    ];

    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    summarySheet["!cols"] = [{ wch: 28 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Generate filename
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `Product_Requests_Export_${timestamp}.xlsx`;

    XLSX.writeFile(workbook, filename);

    showSuccess(
      `Exported ${requests.length} requests (${totalProducts} products) successfully!\n` +
      `Includes: ${totalReturned} returned + ${totalDamaged} damaged items`
    );
  } catch (error) {
    console.error("Export failed:", error);
    showError("Failed to export data. Please try again.");
  }
};


const ProductRequests = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRequests, setExpandedRequests] = useState(new Set());
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [branches, setBranches] = useState([]);

  const { isAdmin } = useAdminAuth();

  // Rejection modal state

  const filteredRequests = requests.filter((req) => {
    if (filterStatus === "all") return true;
    return req.status === filterStatus;
  });

  const totalPages = Math.ceil(filteredRequests.length / ITEMS_PER_PAGE);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    fetchRequests();
  }, [selectedBranch]);

  useEffect(() => {
    if (isAdmin) {
      fetchBranches();
    }
  }, [isAdmin]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`,
      );
      setBranches(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-requests/get-all`,
        {
          params: {
            // send branch ONLY for superadmin
            ...(isAdmin && selectedBranch && { branch: selectedBranch }),
          },
        },
      );
      setRequests(res.data.data || []);
    } catch (err) {
      showError("Failed to fetch product requests");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecision = async (
    requestId,
    productId,
    status,
    productInfo = null,
  ) => {
    const key = `${requestId}-${productId}`;
    setProcessing(key);

    //  Backup previous state (for rollback)
    const prevRequests = [...requests];

    //  OPTIMISTIC UPDATE
    setRequests((prev) =>
      prev.map((req) => {
        if (req._id !== requestId) return req;

        const updatedProducts = req.products.map((p) => {
          if (p.product._id !== productId) return p;

          return {
            ...p,
            status,
            assignedQuantity:
              status === "approved" ? p.requestedQuantity : 0,
          };
        });

        // update overall request status
        const allProcessed = updatedProducts.every(
          (p) => p.status !== "pending"
        );

        return {
          ...req,
          products: updatedProducts,
          status: allProcessed ? "verified" : req.status,
        };
      })
    );

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/product-requests/${requestId}/decision-bulk`,
        {
          products: [{ productId, status }],
        }
      );

      showSuccess(`Product ${status} successfully`);
    } catch (err) {
      //  rollback on error
      setRequests(prevRequests);

      const errorMessage =
        err?.response?.data?.message || "Action failed";

      if (productInfo && errorMessage.toLowerCase().includes("stock")) {
        showError(
          `Insufficient stock! Requested: ${productInfo.requestedQuantity}, Available: ${productInfo.quantity}`
        );
      } else {
        showError(errorMessage);
      }
    } finally {
      setProcessing(null);
    }
  };


  const toggleExpand = (requestId) => {
    const newExpanded = new Set(expandedRequests);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRequests(newExpanded);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock },
      verified: {
        bg: "bg-green-100",
        text: "text-green-700",
        icon: CheckCircle,
      },
      approved: {
        bg: "bg-green-100",
        text: "text-green-700",
        icon: CheckCircle,
      },
      rejected: { bg: "bg-red-100", text: "text-red-700", icon: AlertCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.text}`}
      >
        <Icon size={12} />
        {status}
      </span>
    );
  };

  return (
    <div className="flex flex-col min-w-0 bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className=" py-2">
        <Breadcrumb title1="Products" title2="Product Requests" />
      </div>

      <div className="mb-6 bg-white rounded-lg shadow-md p-4">
        {/* Header with Stats & Filters */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-gray-800">
              Product Requests
            </h2>
            <div className="flex gap-2 text-sm">
              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">
                Total: {requests.length}
              </span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-medium">
                Pending: {requests.filter((r) => r.status === "pending").length}
              </span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                Verified:{" "}
                {requests.filter((r) => r.status === "verified").length}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Filter Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {["all", "pending", "verified"].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    setFilterStatus(status);
                    setCurrentPage(1);
                  }}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition ${filterStatus === status
                    ? "bg-white text-gray-900 shadow"
                    : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Branch Filter (SUPER ADMIN ONLY) */}
            {isAdmin && (
              <select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Branches</option>
                {branches.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.branchName}
                  </option>
                ))}
              </select>
            )}

            {/* Export Button */}
            <button
              onClick={() => exportToExcel(filteredRequests)}
              disabled={filteredRequests.length === 0}
              className="px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              <Download size={18} />
              Export Excel
            </button>
          </div>
        </div>

        {/* Requests Table */}
        {isLoading ? (
          <RequestSkeleton />
        ) : filteredRequests.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr className="text-left text-gray-700">
                    <th className="px-3 py-2 font-semibold w-8"></th>
                    <th className="px-3 py-2 font-semibold">Request ID</th>
                    <th className="px-3 py-2 font-semibold">Branch</th>
                    <th className="px-3 py-2 font-semibold">Staff</th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold text-center">
                      Products
                    </th>
                    <th className="px-3 py-2 font-semibold text-center">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {paginatedRequests.map((request) => {
                    const isExpanded = expandedRequests.has(request._id);
                    const pendingCount = request.products.filter(
                      (p) => p.status === "pending",
                    ).length;

                    return (
                      <>
                        {/* Request Row */}
                        <tr
                          key={request._id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleExpand(request._id)}
                        >
                          <td className="px-3 py-2">
                            {isExpanded ? (
                              <ChevronUp size={16} className="text-gray-500" />
                            ) : (
                              <ChevronDown
                                size={16}
                                className="text-gray-500"
                              />
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">
                            #{request._id.slice(-8).toUpperCase()}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {request.branch?.branchName}
                          </td>
                          <td className="px-3 py-2 text-gray-700">
                            {request.staff?.name}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {new Date(request.createdAt).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                              },
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center gap-1">
                              <Package size={14} className="text-gray-400" />
                              {request.products.length}
                              {pendingCount > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                                  {pendingCount}
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {getStatusBadge(request.status)}
                          </td>
                        </tr>

                        {/* Expanded Products */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="7" className="p-0">
                              <div className="bg-gray-50 px-3 py-2">
                                <table className="w-full text-sm">
                                  <thead className="bg-white border-y">
                                    <tr className="text-left text-gray-600 text-xs">
                                      <th className="px-2 py-1.5">Product</th>
                                      <th className="px-2 py-1.5">Brand</th>
                                      <th className="px-2 py-1.5 text-center">
                                        Requested
                                      </th>
                                      <th className="px-2 py-1.5 text-center">
                                        Stock
                                      </th>
                                      <th className="px-2 py-1.5 text-center">
                                        Type
                                      </th>
                                      <th className="px-2 py-1.5 text-center">Returned</th>
                                      <th className="px-2 py-1.5 text-center">Damaged</th>
                                      <th className="px-2 py-1.5 text-center">
                                        Return Status
                                      </th>
                                      <th className="px-2 py-1.5 text-center">
                                        Status
                                      </th>
                                      <th className="px-2 py-1.5 text-center">
                                        Action
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y capitalize">
                                    {request.products.map((item) => {
                                      const key = `${request._id}-${item.product._id}`;
                                      const showReturned = item.returnedQuantity > 0;
                                      const showDamaged = item.damagedQuantity > 0;

                                      return (
                                        <tr
                                          key={key}
                                          className="hover:bg-gray-50"
                                        >
                                          <td className="px-2 py-2">
                                            <div className="flex items-center gap-2">
                                              <div className="w-8 h-8 bg-gray-100 rounded flex-shrink-0">
                                                {item.product?.productImg ? (
                                                  <img
                                                    src={
                                                      item.product.productImg
                                                    }
                                                    alt=""
                                                    className="w-full h-full object-cover rounded"
                                                  />
                                                ) : (
                                                  <div className="w-full h-full flex items-center justify-center">
                                                    <Package
                                                      size={14}
                                                      className="text-gray-300"
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                              <span className="text-gray-800 font-medium">
                                                {item.product?.productName}
                                              </span>
                                            </div>
                                          </td>
                                          <td className="px-2 py-2 text-gray-600">
                                            {item.product?.brand}
                                          </td>
                                          <td className="px-2 py-2 text-center font-medium">
                                            <span className="text-gray-900">
                                              {item.requestedQuantity}
                                            </span>
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            {(() => {
                                              const available =
                                                item.product?.quantity || 0;
                                              const requested =
                                                item.requestedQuantity || 0;
                                              const isInsufficient =
                                                requested > available;

                                              return (
                                                <div className="flex flex-col items-center gap-0.5">
                                                  <span
                                                    className={`font-semibold ${isInsufficient
                                                      ? "text-red-600"
                                                      : available < 10
                                                        ? "text-orange-600"
                                                        : "text-green-600"
                                                      }`}
                                                  >
                                                    {available}
                                                  </span>
                                                  {isInsufficient && (
                                                    <span className="text-[10px] text-red-600 font-medium">
                                                      Low Stock!
                                                    </span>
                                                  )}
                                                </div>
                                              );
                                            })()}
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            <span
                                              className={`px-2 py-0.5 rounded text-xs font-medium ${item.product
                                                ?.productUsageType ===
                                                "reusable"
                                                ? "bg-green-100 text-green-700"
                                                : "bg-orange-100 text-orange-700"
                                                }`}
                                            >
                                              {item.product
                                                ?.productUsageType ===
                                                "reusable"
                                                ? "Reusable"
                                                : "Use & Throw"}
                                            </span>
                                          </td>
                                          {/* Returned Qty */}
                                          <td className="px-2 py-2 text-center">
                                            {showReturned ? (
                                              <span className="font-medium text-green-700">{item.returnedQuantity}</span>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                          </td>

                                          {/* Damaged Qty */}
                                          <td className="px-2 py-2 text-center">
                                            {showDamaged ? (
                                              <span className="font-medium text-red-700">{item.damagedQuantity}</span>
                                            ) : (
                                              <span className="text-gray-400">—</span>
                                            )}
                                            {item.returnRemark && (
                                              <div className="mt-1 text-xs text-gray-600 italic max-w-xs">
                                                Remark: {item.returnRemark}
                                              </div>
                                            )}
                                          </td>
                                          {/* Return Status */}
                                          <td className="px-2 py-2 text-center">
                                            {item.status !== "approved" ? (
                                              <span className="text-xs text-gray-400">—</span>
                                            ) : item.product?.productUsageType === "use_and_throw" ? (
                                              <span className="text-xs text-gray-400">Not Required</span>
                                            ) : (
                                              <span
                                                className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${item.returnStatus === "returned"
                                                  ? "bg-green-100 text-green-700"
                                                  : item.returnStatus === "damaged"
                                                    ? "bg-red-100 text-red-700"
                                                    : item.returnStatus === "partially_returned"
                                                      ? "bg-purple-100 text-purple-700"
                                                      : item.returnStatus === "pending"
                                                        ? "bg-yellow-100 text-yellow-700"
                                                        : "bg-gray-100 text-gray-600"
                                                  }`}
                                              >
                                                {item.returnStatus === "not_required"
                                                  ? "Not Required"
                                                  : item.returnStatus === "partially_returned"
                                                    ? "Part. Returned"
                                                    : item.returnStatus.charAt(0).toUpperCase() + item.returnStatus.slice(1)}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            {getStatusBadge(item.status)}
                                          </td>
                                          <td className="px-2 py-2 text-center">
                                            {item.status === "pending" &&
                                              request.status !== "verified" ? (
                                              <div className="flex items-center justify-center gap-1">
                                                <button
                                                  disabled={processing === key}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDecision(
                                                      request._id,
                                                      item.product._id,
                                                      "approved",
                                                      "",
                                                      {
                                                        quantity:
                                                          item.product
                                                            ?.quantity,
                                                        requestedQuantity:
                                                          item.requestedQuantity,
                                                        productName:
                                                          item.product
                                                            ?.productName,
                                                      },
                                                    );
                                                  }}
                                                  className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 transition"
                                                  title="Approve"
                                                >
                                                  <Check size={14} />
                                                </button>

                                                <button
                                                  disabled={processing === key}
                                                  onClick={async (e) => {
                                                    e.stopPropagation();

                                                    const confirmed = await confirmAction({
                                                      title: "Reject this product?",
                                                      text: "This action cannot be undone.",
                                                      confirmText: "Reject",
                                                      cancelText: "Cancel",
                                                    });

                                                    if (!confirmed) return;

                                                    handleDecision(
                                                      request._id,
                                                      item.product._id,
                                                      "rejected"
                                                    );
                                                  }}
                                                  className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 transition"
                                                  title="Reject"
                                                >
                                                  <X size={14} />
                                                </button>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-gray-400">
                                                —
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t">
                <p className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages} ({filteredRequests.length}{" "}
                  requests)
                </p>

                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                  >
                    Previous
                  </button>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Loading Skeleton
const RequestSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
    ))}
  </div>
);

// Empty State
const EmptyState = () => (
  <div className="py-12 text-center">
    <Package size={48} className="mx-auto mb-3 text-gray-300" />
    <p className="text-gray-500">No product requests found</p>
  </div>
);

export default ProductRequests;
